import React from 'react';

import { useTranslation } from '@/i18n';
import { PermissionGuard } from '@/components/layout';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { ExportDropdown } from '@/components/ui/filters/ExportDropdown';
import { FilterDropdown } from '@/components/ui/filters/FilterDropdown';
import { IconScout } from '@/components/ui/images/IconScout';
import { Selector } from '@/components/ui/inputs/selector';
import Table from '@/components/ui/tables/Table';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import type { HolidayItem } from '@/api/domains/attendance';
import { normalizeNumericSelectValue } from '@/utils/formHelpers';


interface HolidayListFilters {
  year: number | null;
  month: number | null;
  holidayTypeId: number | null;
}

interface HolidayListTabProps {
  rows: HolidayItem[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  columns: TableColumn<HolidayItem>[];
  actions: TableAction<HolidayItem>[];
  onSearch: (term: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  loading: boolean;
  openCreatePanel: () => void;
  onExport: () => Promise<void>;
  isExporting: boolean;
  yearOptions: { value: string; label: string }[];
  monthOptions: { value: string; label: string }[];
  holidayTypeOptions: { value: string; label: string }[];
  filters: HolidayListFilters;
  setFilters: (filters: HolidayListFilters) => void;
}

export const HolidayListTab: React.FC<HolidayListTabProps> = ({
  rows,
  total,
  page,
  pageSize,
  setPage,
  setPageSize,
  columns,
  actions,
  onSearch,
  onSortChange,
  loading,
  openCreatePanel,
  onExport,
  isExporting,
  yearOptions,
  monthOptions,
  holidayTypeOptions,
  filters,
  setFilters,
}) => {
  const { t: translate } = useTranslation();

  return (
    <div data-testid="holiday-list-tab">
      <div className="mb-4">
        <PermissionGuard action="create">
          <PrimaryButton
            iconLeft={<IconScout name="plus" />}
            onClick={openCreatePanel}
            data-testid="create-holiday-button"
          >
            {translate('attendance:holiday.actions.create')}
          </PrimaryButton>
        </PermissionGuard>
      </div>

      <div className="flex flex-wrap gap-4 justify-between items-start mb-4">
        <FilterDropdown
          extraFilters={
            <div className="flex flex-col gap-3 pt-2">
              <Selector
                label={translate('attendance:holiday.filters.year')}
                options={yearOptions}
                value={filters.year !== null ? String(filters.year) : ''}
                onChange={(value: string | string[]) =>
                  setFilters({ ...filters, year: normalizeNumericSelectValue(value) ?? null })
                }
                data-testid="holiday-year-filter"
              />
              <Selector
                label={translate('attendance:holiday.filters.month')}
                options={monthOptions}
                value={filters.month !== null ? String(filters.month) : ''}
                onChange={(value: string | string[]) =>
                  setFilters({ ...filters, month: normalizeNumericSelectValue(value) ?? null })
                }
                data-testid="holiday-month-filter"
              />
              <Selector
                label={translate('attendance:holiday.filters.holidayType')}
                options={holidayTypeOptions}
                value={filters.holidayTypeId !== null ? String(filters.holidayTypeId) : ''}
                onChange={(value: string | string[]) => {
                  const holidayTypeId = normalizeNumericSelectValue(value) ?? null;
                  setFilters({ ...filters, holidayTypeId });
                }}
                data-testid="holiday-type-filter"
              />
            </div>
          }
        />
        <ExportDropdown
          onExport={onExport}
          placement="right"
          isLoading={isExporting}
          availableFormats={['EXCEL']}
        />
      </div>

      <Table
        columns={columns}
        data={rows}
        totalRows={total}
        searchable
        onSearch={onSearch}
        onSortChange={onSortChange}
        serverSideSort
        pagination={{
          page,
          pageSize,
          total,
          onChange: setPage,
          onPageSizeChange: setPageSize,
        }}
        isLoading={loading}
        actions={actions}
        data-testid="holiday-table"
      />
    </div>
  );
};

export default HolidayListTab;
